import { command } from "../../../app/command";
import { query } from "../../../app/query";
import { NameIdOption } from "../../../common/types/commonTypes";
import { EventPayload } from "../../../common/types/Event";
import { Options } from "../../../common/types/requestOptions";

export interface TargetingKeys {
  custCompany: NameIdOption;
  custOrgs: NameIdOption[];
  workLocs: NameIdOption[];
  workTitles: string[];
}

export interface TargetingKeysCmd {
  custCompanyId: string;
  custOrgs: string[];
  workLocs: string[];
  workTitles: string[];
}

export interface SalaryDateSpec {
  from: string;
  components: Record<string, string>;
  zonedComponents?: Record<string, Record<string, string>>;
  massUpdateId?: string;
  tesSalaryTableSelection?: {
    tesSalaryTableId: string;
    rowId: string;
    columnId: string;
    mode: "FULL_SALARY" | "WITH_COMPS" | "INFO_ONLY";
  } | null;
}

export interface DateBasedSalary {
  ranges: SalaryDateSpec[];
}

export interface TargetedShiftSalaryRow {
  keys: TargetingKeys;
  salary: DateBasedSalary;
}

export interface TargetedShiftSpec {
  version: number;
  targetingRules: TargetingKeys[];
  salaries: TargetedShiftSalaryRow[];
  variationValidationAssignmentId: string | null;
}

export interface ChangeTargetedShiftSalaryRowCmd {
  employmentId: string;
  version: number;
  keys: TargetingKeysCmd;
  salary: DateBasedSalary | null;
}

export interface DeleteTargetedShiftSalaryRowCmd {
  employmentId: string;
  version: number;
  keys: TargetingKeysCmd;
}

export interface TargetedShiftSalaryRowChanged {
  keys: TargetingKeys;
  salary: DateBasedSalary | null;
}

export type CanSetTargetingForEmployment = boolean;

export interface CanSetTargetingForEmploymentPayload {
  employmentId: string;
  validationAssignmentId: string;
}

export const getEmpTargetedShiftSpec = (
  filters: { employmentId: string },
  options: Options = {},
): Promise<TargetedShiftSpec> =>
  query<{ employmentId: string }, TargetedShiftSpec>(
    "/projections/qry/empTargetedShiftSpec",
    filters,
    options,
  );

export const changeTargetedShiftSalaryRow = (
  body: ChangeTargetedShiftSalaryRowCmd,
  options: Options = {},
): Promise<EventPayload<TargetedShiftSalaryRowChanged>[]> =>
  command<ChangeTargetedShiftSalaryRowCmd, TargetedShiftSalaryRowChanged>(
    "/working/cmd/chgTargetedShiftSalaryRow",
    body,
    options,
  );

export const deleteTargetedShiftSalaryRow = (
  body: DeleteTargetedShiftSalaryRowCmd,
  options: Options = {},
): Promise<EventPayload<TargetedShiftSalaryRowChanged>[]> =>
  command<DeleteTargetedShiftSalaryRowCmd, TargetedShiftSalaryRowChanged>(
    "/working/cmd/delTargetedShiftSalaryRow",
    body,
    options,
  );

export const getCanSetTargetingForEmployment = (
  params: CanSetTargetingForEmploymentPayload,
  options: Options = {},
): Promise<CanSetTargetingForEmployment> =>
  query<CanSetTargetingForEmploymentPayload, CanSetTargetingForEmployment>(
    "/projections/qry/canSetTargetingForEmployment",
    params,
    options,
  );
